import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { computeAnchoredOverlayPosition } from '../../utils/anchoredOverlayPosition';

interface InfoTooltipProps {
  label: string;
  children: React.ReactNode;
}

/** Grace period so the pointer can travel from the icon onto the panel without closing it. */
const CLOSE_DELAY_MS = 150;

/**
 * Explanatory hint attached to an info icon.
 *
 * The panel is portalled so it stays readable inside scrolling containers that hide their
 * overflow. Because a portalled panel is not a DOM descendant of the trigger, leaving the icon
 * would otherwise close it before it could be read, so closing is delayed and cancelled while
 * the pointer is over the panel.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({ label, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimeout = useRef<number | undefined>(undefined);
  const panelId = useId();

  const open = () => {
    if (closeTimeout.current) window.clearTimeout(closeTimeout.current);
    setIsOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimeout.current) window.clearTimeout(closeTimeout.current);
    closeTimeout.current = window.setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => {
    if (closeTimeout.current) window.clearTimeout(closeTimeout.current);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const reposition = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;

      const { top, left } = computeAnchoredOverlayPosition(
        trigger.getBoundingClientRect(),
        panel.offsetWidth,
        panel.offsetHeight,
        { width: window.innerWidth, height: window.innerHeight }
      );
      setPosition({ top, left });
    };

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    const raf = requestAnimationFrame(reposition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('keydown', onEsc);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? panelId : undefined}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={scheduleClose}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className="flex items-center text-slate-gray hover:text-electric-blue transition-colors"
      >
        <Info size={14} aria-hidden="true" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="tooltip"
            onMouseEnter={open}
            onMouseLeave={scheduleClose}
            style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 9999 }}
            className="w-72 space-y-2 rounded-xl bg-charcoal px-3 py-2.5 text-xs leading-relaxed text-white shadow-xl"
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
};

export default InfoTooltip;
